import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams } from "react-router-dom";
import TopNav from "../../commonUI/TopHeader/TopHeader";
import TabNav from "../../commonUI/TabNav/TabNav";
import SearchFilterBar from "../../commonUI/SearchFilterBar/SearchFilterBar";
import DataTable from "../../commonUI/DataTable/DataTable";
import Pagination from "../../commonUI/Pagination/Pagination";
import {
  defaultAvatarImage,
  imageFormat,
  serverUrl,
  thumbSuffix,
  userImagesFolder,
} from "../../../config/env";
import {
  SUPPORT_COLUMNS,
  SUPPORT_SORT_FIELDS,
  SUPPORT_USER_TYPE_OPTIONS,
  SUPPORT_STATUS_OPTIONS,
  STATIC_SUPPORT_CONVERSATIONS,
} from "../../../config/constants";

const Support = ({ userDetails }) => {
  const { type } = useParams();
  const navigate  = useNavigate();

  const [activeTab,      setActiveTab]      = useState("supportChat");
  const [searchValue,    setSearchValue]    = useState("");
  const [search,         setSearch]         = useState("");
  const [sort,           setSort]           = useState("sentAt=-1");
  const [userTypeFilter, setUserTypeFilter] = useState("any");
  const [statusFilter,   setStatusFilter]   = useState("any");
  const [dateRange,      setDateRange]      = useState({ start: null, end: null });
  const [pageSize,       setPageSize]       = useState(15);
  const [totalPages,     setTotalPages]     = useState(1);
  const [page,           setPage]           = useState(1);
  const [inputValue,     setInputValue]     = useState(1);

  const handleSearch = useCallback(() => {
    setSearch(searchValue);
    setPage(1);
  }, [searchValue]);

  const handleReset = () => {
    setSearchValue("");
    setSearch("");
    setUserTypeFilter("any");
    setStatusFilter("any");
    setDateRange({ start: null, end: null });
    setPage(1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
  };

  const sortSupport = useCallback(
    (event, field) => {
      if (event) event.preventDefault();
      const currentSort = SUPPORT_SORT_FIELDS[field];
      if (!currentSort) return;
      const newSort = sort === currentSort.asc ? currentSort.desc : currentSort.asc;
      setSearch("");
      setSort(newSort);
      setPage(1);
    },
    [sort]
  );

  const getSortClass = useCallback(
    (key) => {
      if (sort === `${key}=1`)  return "sort-button sorting-icon sorting-icon-up";
      if (sort === `${key}=-1`) return "sort-button sorting-icon sorting-icon-down";
      return "sort-button";
    },
    [sort]
  );

  const handleView = useCallback(
    (conversation) => {
      const userId =
        activeTab === "supportChat"
          ? conversation?.supportChatUser?.shortId
          : conversation?.disputeUser?.shortId;

      navigate(`/conversation/support/${userId}`, {
        state: {
          conversation,
          activeTab,
          conversationType: activeTab === "supportChat" ? "support" : "dispute",
        },
      });
    },
    [activeTab, navigate]
  );

  const getAvatarSrc = useCallback((conversation) => {
    const user = conversation?.supportChatUser;
    if (user?.avatar && user.avatar !== "") {
      return `${serverUrl}${userImagesFolder}/${user.shortId}/${user.avatar}${thumbSuffix}${imageFormat}`;
    }
    return `../img/${defaultAvatarImage}${imageFormat}`;
  }, []);

  const renderUnread = (count) =>
    count > 0 ? (
      <span className="unread-badge">{count}</span>
    ) : (
      <span>–</span>
    );

  const renderSupportChat = useCallback(
    (conversation, index) => (
      <tr key={index}>
        <td>
          <div className="d-flex align-items-center g-12">
            <img
              src={getAvatarSrc(conversation)}
              alt="avatar"
              className="avatar-pic"
              onError={(e) => { e.target.src = defaultAvatarImage; }}
            />
            <div className="text-capitalize">
              {conversation?.supportChatUser?.name || "-"}
              <br />
              <span className="text-sm text-gray-600">
                {conversation?.supportChatUser?.shortId || "-"}
              </span>
            </div>
          </div>
        </td>
        <td>
          <div className="conversation-unread">
            {renderUnread(conversation?.supportChatUnreadCount)}
          </div>
        </td>
        <td>
          <div
            className={`message-container ${
              conversation?.supportChatUser?.type === "client"
                ? "message-client"
                : conversation?.supportChatUser?.type === "driver"
                ? "message-driver"
                : "message-admin"
            }`}
          >
            <p>{conversation?.supportChatMessage || "-"}</p>
          </div>
        </td>
        <td>
          <span>{conversation?.supportChatSentAt?.date || "-"}</span>
          <br />
          <span className="text-sm text-gray-600">
            {conversation?.supportChatSentAt?.time || "-"}
          </span>
        </td>
        <td>
          <button
            type="button"
            className="custom-btn"
            onClick={() => handleView(conversation)}
          >
            View
          </button>
        </td>
      </tr>
    ),
    [getAvatarSrc, handleView]
  );

  const renderDisputes = useCallback(
    (conversation, index) => (
      <tr key={index}>
        <td>
          <div className="d-flex align-items-center g-12">
            <img
              src={getAvatarSrc(conversation)}
              alt="avatar"
              className="avatar-pic"
              onError={(e) => { e.target.src = defaultAvatarImage; }}
            />
            <div className="text-capitalize">
              {conversation?.disputeUser?.name || "-"}
              <br />
              <span className="text-sm text-gray-600">
                {conversation?.disputeUser?.shortId || "-"}
              </span>
            </div>
          </div>
        </td>
        <td>
          <div className="conversation-unread">
            {renderUnread(conversation?.disputeUnreadCount)}
          </div>
        </td>
        <td>
          <div
            className={`message-container ${
              conversation?.disputeUser?.type === "client"
                ? "message-client"
                : conversation?.disputeUser?.type === "driver"
                ? "message-driver"
                : "message-admin"
            }`}
          >
            <p>{conversation?.disputeMessage || "-"}</p>
          </div>
        </td>
        <td>
          <span>{conversation?.disputeSentAt?.date || "-"}</span>
          <br />
          <span className="text-sm text-gray-600">
            {conversation?.disputeSentAt?.time || "-"}
          </span>
        </td>
        <td>
          <button
            type="button"
            className="custom-btn"
            onClick={() => handleView(conversation)}
          >
            View
          </button>
        </td>
      </tr>
    ),
    [getAvatarSrc, handleView]
  );

  const tabs = useMemo(
    () => [
      { key: "supportChat", label: "Support Chat", count: STATIC_SUPPORT_CONVERSATIONS.length },
      { key: "disputes",    label: "Disputes",     count: STATIC_SUPPORT_CONVERSATIONS.length },
    ],
    []
  );

  const filters = useMemo(
    () => [
      {
        type:        "search",
        key:         "search",
        label:       "Search",
        value:       searchValue,
        onChange:    setSearchValue,
        placeholder: "Search...",
        onSubmit:    handleSearch,
      },
      {
        type:        "select",
        key:         "userType",
        label:       "User Type",
        value:       userTypeFilter,
        onChange:    setUserTypeFilter,
        placeholder: "Select User Type",
        options:     SUPPORT_USER_TYPE_OPTIONS,
      },
      {
        type:        "select",
        key:         "status",
        label:       "Status",
        value:       statusFilter,
        onChange:    setStatusFilter,
        placeholder: "Select Status",
        options:     SUPPORT_STATUS_OPTIONS,
      },
      {
        type:        "dateRange",
        key:         "registeredOn",
        label:       "Registered on",
        value:       dateRange,
        onChange:    setDateRange,
        placeholder: "Anytime",
      },
    ],
    [searchValue, userTypeFilter, statusFilter, dateRange, handleSearch]
  );

  const activeRenderRow =
    activeTab === "supportChat" ? renderSupportChat : renderDisputes;

  return (
    <>
      <TopNav title="Support" />
      <div className="global-body">
        <div className="global-body-container">
          <TabNav tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />

          <SearchFilterBar filters={filters} onReset={handleReset} />

          <DataTable
            data={STATIC_SUPPORT_CONVERSATIONS}
            columns={SUPPORT_COLUMNS}
            renderRow={activeRenderRow}
            onSort={sortSupport}
            getSortClass={getSortClass}
            emptyMessage="No conversations found"
            tableClassName="table clientTable supportTable"
            colSpan={5}
          />

          <Pagination
            pageSize={pageSize}
            totalPages={totalPages}
            page={page}
            setPage={setPage}
            inputValue={inputValue}
            setInputValue={setInputValue}
            setPageSize={setPageSize}
          />
        </div>
      </div>
    </>
  );
};

Support.propTypes = {
  userDetails: PropTypes.object.isRequired,
};

export default Support;
